import type { EstadoDeCobro } from "./estadoDeCobro";

/**
 * TEMPORAL — simulación de los avisos de cobro rechazado para verlos en una cuenta real sin tocar sus
 * datos (ADR-2026-10-26-simular-avisos-de-cobro). Sacar cuando estén verificados.
 *
 * `?simular-cobro=<caso>` en cualquier URL de la app lo prende para esa pestaña (sessionStorage, así
 * sobrevive a la navegación); `?simular-cobro=no` lo apaga. Casos: `rechazo`, `rechazo-stripe`,
 * `baja`, `baja-mp`, `baja-hecha`,
 * y con facturación por marca `marca` (dueño, dos marcas) y `marca-miembro` (baja de una marca vista
 * por un miembro).
 *
 * Sólo cambia lo que se DIBUJA: el aviso simulado lleva `simulado: true` y sus botones no cobran ni
 * abren nada (ver `AvisoDeCobro`). No hace falta restringirlo por cuenta: no escribe nada y el único
 * que lo ve es quien pone el parámetro en su propia pestaña.
 */

const CLAVE = "simular-cobro";
const DIA = 86_400_000;

export const CASOS_DE_SIMULACION = [
  "rechazo",
  "rechazo-stripe",
  "baja",
  "baja-mp",
  "baja-hecha",
  "marca",
  "marca-miembro",
] as const;
type Caso = (typeof CASOS_DE_SIMULACION)[number];

function casoActivo(): Caso | null {
  if (typeof window === "undefined") return null;
  try {
    const param = new URLSearchParams(window.location.search).get(CLAVE);
    if (param === "no") {
      window.sessionStorage.removeItem(CLAVE);
      return null;
    }
    if (param && (CASOS_DE_SIMULACION as readonly string[]).includes(param)) {
      window.sessionStorage.setItem(CLAVE, param);
      return param as Caso;
    }
    const guardado = window.sessionStorage.getItem(CLAVE);
    return guardado && (CASOS_DE_SIMULACION as readonly string[]).includes(guardado) ? (guardado as Caso) : null;
  } catch {
    return null;
  }
}

function estadoSimulado(caso: Caso, ahora: number): EstadoDeCobro {
  switch (caso) {
    case "rechazo":
      return { tipo: "rechazo", plataforma: "rebill", motivo: "fondos", proximoReintento: new Date(ahora + 2 * DIA), tarjeta: "1234", simulado: true };
    case "rechazo-stripe":
      return { tipo: "rechazo", plataforma: "stripe", motivo: null, proximoReintento: null, tarjeta: null, simulado: true };
    case "baja":
      return { tipo: "baja_programada", plataforma: "rebill", motivo: "tarjeta_vencida", fechaBaja: new Date(ahora + 12 * DIA), tarjeta: "1234", simulado: true };
    case "baja-mp":
      return { tipo: "baja_programada", plataforma: "mp", motivo: "fondos", fechaBaja: new Date(ahora + 25 * DIA), tarjeta: "5678", simulado: true };
    case "marca":
      return {
        tipo: "rechazo",
        plataforma: "rebill",
        motivo: "fondos",
        proximoReintento: new Date(ahora + 2 * DIA),
        tarjeta: "1234",
        marca: { brandId: "simulacion", nombre: "Tu marca", esDueno: true, otras: ["Otra marca"] },
        simulado: true,
      };
    case "marca-miembro":
      return {
        tipo: "baja_programada",
        plataforma: "rebill",
        motivo: "tarjeta_vencida",
        fechaBaja: new Date(ahora + 12 * DIA),
        tarjeta: "1234",
        marca: { brandId: "simulacion", nombre: "Tu marca", esDueno: false, otras: [] },
        simulado: true,
      };
    case "baja-hecha":
      return { tipo: "dado_de_baja", plataforma: "rebill", claveCierre: `simulacion:${ahora}`, simulado: true };
  }
}

/** Si hay una simulación prendida, la devuelve en lugar del estado real. */
export function conSimulacion(estado: EstadoDeCobro | null): EstadoDeCobro | null {
  const caso = casoActivo();
  return caso ? estadoSimulado(caso, Date.now()) : estado;
}
